import * as msal from "@azure/msal-browser";

const GRAPH_SCOPES = ["User.Read"];
const TOKEN_VALID_KEY = "auth_token_valid";

/**
 * Erro lançado quando a aquisição silenciosa de token falha
 * (refresh token expirado ou conta removida).
 *
 * @remarks
 * `interactionRequired` indica se a sessão precisa de novo login interativo;
 * quando `false`, a falha pode ser transitória (rede, backend indisponível).
 *
 * @example
 * ```typescript
 * if (err instanceof AuthError && err.interactionRequired) {
 *   // redireciona para o login
 * }
 * ```
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly originalError?: any,
    public readonly interactionRequired: boolean = false
  ) {
    super(message);
    this.name = "AuthError";
  }
}

/**
 * Marca localmente que o token de acesso atual é válido.
 *
 * @example
 * markTokenValid();
 */
export function markTokenValid(): void {
  localStorage.setItem(TOKEN_VALID_KEY, "1");
}

/**
 * Remove a marcação local de token válido (sessão expirada ou desconectada).
 *
 * @example
 * markTokenInvalid();
 */
export function markTokenInvalid(): void {
  localStorage.removeItem(TOKEN_VALID_KEY);
}

/**
 * Adquire um access token do Graph silenciosamente via MSAL (sem popup),
 * atualizando a marcação local de validade do token.
 *
 * @param msalInstance - Instância MSAL autenticada.
 * @param account - Conta MSAL usada para a aquisição do token.
 * @param forceRefresh - Se `true`, ignora o cache de access token do MSAL.
 * @returns O resultado da aquisição, incluindo `accessToken` e `expiresOn`.
 * @throws {@link AuthError} Se a aquisição silenciosa falhar.
 *
 * @example
 * const result = await acquireGraphTokenSilently(msalInstance, account);
 */
export async function acquireGraphTokenSilently(
  msalInstance: msal.PublicClientApplication,
  account: msal.AccountInfo,
  forceRefresh: boolean = false
): Promise<msal.AuthenticationResult> {
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: GRAPH_SCOPES,
      account,
      forceRefresh,
    });
    markTokenValid();
    return result;
  } catch (err: any) {
    const interactionRequired =
      err?.errorCode === "interaction_required" || err?.name === "InteractionRequiredAuthError";

    if (interactionRequired) {
      markTokenInvalid();
    }

    throw new AuthError(
      interactionRequired
        ? "Sessão expirada. Faça login novamente."
        : `Falha ao adquirir token: ${err?.errorCode || err?.message}`,
      err,
      interactionRequired
    );
  }
}
